import { lazy, Suspense, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Layout from "./components/Layout";
import BreadcrumbStructuredData from "./components/BreadcrumbStructuredData";
import ScrollToTop from "./components/ScrollToTop";
import Home from "./pages/Home";

// Lazy load all non-critical global widgets (deferred after first paint)
const CookieConsent = lazy(() => import("./components/CookieConsent"));
const WhatsAppButton = lazy(() => import("./components/WhatsAppButton"));
const StickyMobileCta = lazy(() => import("./components/StickyMobileCta"));
const ChatbotWidget = lazy(() => import("./components/ChatbotWidget").then(m => ({ default: m.ChatbotWidget })));
const BackToTop = lazy(() => import("./components/effects").then(m => ({ default: m.BackToTop })));

// All other pages lazy-loaded — only downloaded when the user navigates to them
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const Industries = lazy(() => import("./pages/Industries"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const BulkSms = lazy(() => import("./pages/services/BulkSms"));
const TransactionalSms = lazy(() => import("./pages/services/TransactionalSms"));
const PromotionalSms = lazy(() => import("./pages/services/PromotionalSms"));
const OtpSms = lazy(() => import("./pages/services/OtpSms"));
const WhatsAppBusiness = lazy(() => import("./pages/services/WhatsAppBusiness"));
const GoogleAds = lazy(() => import("./pages/services/GoogleAds"));
const SeoServices = lazy(() => import("./pages/services/SeoServices"));
const VoiceCall = lazy(() => import("./pages/services/VoiceCall"));
const RcsMessaging = lazy(() => import("./pages/services/RcsMessaging"));
const EmailMarketing = lazy(() => import("./pages/services/EmailMarketing"));
const SocialMediaMarketing = lazy(() => import("./pages/services/SocialMediaMarketing"));
const WebsiteDesign = lazy(() => import("./pages/services/WebsiteDesign"));
const MetaAds = lazy(() => import("./pages/services/MetaAds"));
const LeadGeneration = lazy(() => import("./pages/services/LeadGeneration"));
const ContentMarketing = lazy(() => import("./pages/services/ContentMarketing"));
const LocalSeo = lazy(() => import("./pages/services/LocalSeo"));
const DltRegistration = lazy(() => import("./pages/services/DltRegistration"));
const SocialMediaMonetization = lazy(() => import("./pages/services/SocialMediaMonetization"));
const TollFreeNumber = lazy(() => import("./pages/services/TollFreeNumber"));
const IvrSolutions = lazy(() => import("./pages/services/IvrSolutions"));
const MissedCallServices = lazy(() => import("./pages/services/MissedCallServices"));
const YoutubeAds = lazy(() => import("./pages/services/YoutubeAds"));
const ResellerProgram = lazy(() => import("./pages/services/ResellerProgram"));
const InternationalBulkSms = lazy(() => import("./pages/services/InternationalBulkSms"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const Faq = lazy(() => import("./pages/Faq"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const DltRegistrationGuide = lazy(() => import("./pages/blog/DltRegistrationGuide"));
const DltTemplateApproval = lazy(() => import("./pages/blog/DltTemplateApproval"));
const PromotionalVsTransactional = lazy(() => import("./pages/blog/PromotionalVsTransactional"));
const DLTGuide = lazy(() => import("./pages/DLTGuide"));
const ComparisonMsg91 = lazy(() => import("./pages/ComparisonMsg91"));
const ComparisonTextlocal = lazy(() => import("./pages/ComparisonTextlocal"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Tools
const WhatsAppScrubber = lazy(() => import("./pages/tools/WhatsAppScrubber"));
const SmsCharacterCounter = lazy(() => import("./pages/tools/SmsCharacterCounter"));
const SeoChecker = lazy(() => import("./pages/tools/SeoChecker"));
const DltValidator = lazy(() => import("./pages/DltValidator"));
const TemplateGenerator = lazy(() => import("./pages/TemplateGenerator"));

// Linkable assets
const ApiDocs = lazy(() => import("./pages/ApiDocs"));
const WriteForUs = lazy(() => import("./pages/WriteForUs"));
const SmsMarketingStatistics = lazy(() => import("./pages/SmsMarketingStatistics"));
const Resources = lazy(() => import("./pages/Resources"));
const Tools = lazy(() => import("./pages/Tools"));
// Industry landing pages
const BankingFinance = lazy(() => import("./pages/industries/BankingFinance"));
const ECommerce = lazy(() => import("./pages/industries/ECommerce"));
const Healthcare = lazy(() => import("./pages/industries/Healthcare"));
const Education = lazy(() => import("./pages/industries/Education"));
const RealEstate = lazy(() => import("./pages/industries/RealEstate"));
const Retail = lazy(() => import("./pages/industries/Retail"));
const Logistics = lazy(() => import("./pages/industries/Logistics"));
const RestaurantsFood = lazy(() => import("./pages/industries/RestaurantsFood"));
const TravelTourism = lazy(() => import("./pages/industries/TravelTourism"));
const Manufacturing = lazy(() => import("./pages/industries/Manufacturing"));
const TechnologySaas = lazy(() => import("./pages/industries/TechnologySaas"));
const Automobiles = lazy(() => import("./pages/industries/Automobiles"));

// Keyword landing pages (Phase 2 SEO)
const BulkSmsIndia = lazy(() => import("./pages/BulkSmsIndia"));
const DltCompliance = lazy(() => import("./pages/DltCompliance"));
const WhatsAppApiMarketing = lazy(() => import("./pages/WhatsAppApiMarketing"));
// Minimal loading fallback — keeps layout shift near zero
function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-8 h-8 border-3 border-saffron border-t-transparent rounded-full animate-spin" />
    </div>
  );
}
function Router() {
  // make sure to consider if you need authentication for certain routes
  return (
    <>
      <BreadcrumbStructuredData />
      <Layout>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Switch>
          <Route path="/" component={Home} />
          <Route path="/about" component={About} />
          <Route path="/services" component={Services} />
          <Route path="/industries" component={Industries} />
          <Route path="/testimonials" component={Testimonials} />
          <Route path="/case-studies" component={CaseStudies} />
          <Route path="/case-studies/quickcart-ecommerce-sms" component={CaseStudies} />
          <Route path="/case-studies/medlife-hospitals-sms" component={CaseStudies} />
          <Route path="/case-studies/swiftlogistics-sms" component={CaseStudies} />
          <Route path="/bulk-sms" component={BulkSms} />
          <Route path="/transactional-sms" component={TransactionalSms} />
          <Route path="/promotional-sms" component={PromotionalSms} />
          <Route path="/otp-sms" component={OtpSms} />
          <Route path="/whatsapp-business-api" component={WhatsAppBusiness} />
          <Route path="/google-ads-services" component={GoogleAds} />
          <Route path="/seo-services" component={SeoServices} />
          <Route path="/voice-call" component={VoiceCall} />
          <Route path="/rcs-messaging" component={RcsMessaging} />
          <Route path="/email-marketing" component={EmailMarketing} />
          <Route path="/social-media-marketing" component={SocialMediaMarketing} />
          <Route path="/website-design" component={WebsiteDesign} />
          <Route path="/meta-ads" component={MetaAds} />
          <Route path="/lead-generation" component={LeadGeneration} />
          <Route path="/content-marketing" component={ContentMarketing} />
          <Route path="/local-seo" component={LocalSeo} />
          <Route path="/dlt-registration" component={DltRegistration} />
          <Route path="/social-media-monetization" component={SocialMediaMonetization} />
          <Route path="/services/toll-free-number" component={TollFreeNumber} />
          <Route path="/services/ivr-solutions" component={IvrSolutions} />
          <Route path="/services/missed-call-services" component={MissedCallServices} />
          <Route path="/services/youtube-ads" component={YoutubeAds} />
          <Route path="/reseller-program" component={ResellerProgram} />
          <Route path="/international-bulk-sms" component={InternationalBulkSms} />
          <Route path="/pricing" component={Pricing} />
          <Route path="/contact" component={Contact} />
          <Route path="/blog" component={Blog} />
          <Route path="/blog/dlt-registration-guide" component={DltRegistrationGuide} />
          <Route path="/blog/dlt-template-approval-process" component={DltTemplateApproval} />
          <Route path="/blog/promotional-vs-transactional-sms-india" component={PromotionalVsTransactional} />
          <Route path="/dlt-guide" component={DLTGuide} />
          <Route path="/comparison/msg91" component={ComparisonMsg91} />
          <Route path="/comparison/textlocal" component={ComparisonTextlocal} />
          <Route path="/tools/dlt-validator" component={DltValidator} />
          <Route path="/tools/template-generator" component={TemplateGenerator} />
          <Route path="/faq" component={Faq} />
          <Route path="/privacy-policy" component={PrivacyPolicy} />
          <Route path="/terms-conditions" component={TermsConditions} />
          <Route path="/refund-policy" component={RefundPolicy} />
          <Route path="/cookie-policy" component={CookiePolicy} />
          <Route path="/disclaimer" component={Disclaimer} />
          <Route path="/industries/banking-finance" component={BankingFinance} />
          <Route path="/industries/e-commerce" component={ECommerce} />
          <Route path="/industries/healthcare" component={Healthcare} />
          <Route path="/industries/education" component={Education} />
          <Route path="/industries/real-estate" component={RealEstate} />
          <Route path="/industries/retail" component={Retail} />
          <Route path="/industries/logistics" component={Logistics} />
          <Route path="/industries/restaurants-food" component={RestaurantsFood} />
          <Route path="/industries/travel-tourism" component={TravelTourism} />
          <Route path="/industries/manufacturing" component={Manufacturing} />
          <Route path="/industries/technology-saas" component={TechnologySaas} />
          <Route path="/industries/automobiles" component={Automobiles} />
          <Route path="/tools/whatsapp-number-scrubber" component={WhatsAppScrubber} />
          <Route path="/tools/sms-character-counter" component={SmsCharacterCounter} />
          <Route path="/tools/seo-checker" component={SeoChecker} />
          <Route path="/write-for-us" component={WriteForUs} />
          <Route path="/sms-marketing-statistics-india" component={SmsMarketingStatistics} />
          <Route path="/api-docs" component={ApiDocs} />
          <Route path="/resources" component={Resources} />
          <Route path="/tools" component={Tools} />
          {/* Keyword landing pages */}
          <Route path="/bulk-sms-india" component={BulkSmsIndia} />
          <Route path="/dlt-compliance" component={DltCompliance} />
          <Route path="/whatsapp-api-marketing" component={WhatsAppApiMarketing} />
          <Route component={NotFound} />
          </Switch>
        </Suspense>
      </Layout>
    </>
  );
}

// Deferred widgets — only mount after the page has been interactive for 3s
function DeferredWidgets() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setShow(true), 3000);
    return () => clearTimeout(id);
  }, []);
  if (!show) return null;
  return (
    <Suspense fallback={null}>
      <BackToTop />
      <ChatbotWidget />
      <WhatsAppButton />
      <StickyMobileCta />
      <CookieConsent />
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          <Router />
          <DeferredWidgets />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
